import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { TradeFilterSelectFullWidth } from "@/components/trade-filter-selects";
import {
  combineReportStrategyPolicies,
  listReportStrategyPolicyVersions,
  previewCombineReportStrategyPolicies,
} from "../../../services/reportsApi";
import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import type { CombinePoliciesPreviewResponse } from "../../../types/reportStrategyPine";
import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import {
  combineStackVisibilityFromPolicyVersions,
  combineStackVisibilityFromSourceKinds,
  type CombineStackVisibility,
} from "../../../utils/combineStackVisibility";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { workspaceNodeTitle } from "./workspaceGraphUtils";
import {
  buildCombinePolicySources,
  classifyCombinePolicySourceKind,
  expandCombineSelectionToSourcePolicies,
  listCombinablePolicyNodes,
  MIN_COMBINED_POLICY_SOURCES,
  resolveAdoptedReportFromPolicySelection,
  resolveCombineHomeWorkspaceId,
  strategyIdForCombineCandidate,
} from "./workspaceCombinedPolicyUtils";

export type WorkspaceCombinePoliciesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  /** Strategy federated canvas uses cross-workspace `sources[]` + homeWorkspaceId. */
  viewMode?: "account" | "strategy";
  nodes: WorkspaceNode[];
  edges: WorkspaceEdge[];
  /** Policy node that opened the dialog (always included first). */
  anchorPolicyNode: WorkspaceNode | null;
  /** Pre-checked policy node ids (ordered). First should match the adopted-report anchor. */
  initialSelectedIds?: string[];
  onCombined: (result: {
    policyVersionId: string;
    sourcePolicyNodeIds: string[];
    adoptedReportId: string | null;
    title?: string | null;
    resolvedCandleTimeframe?: string | null;
    workspaceNode?: WorkspaceNode;
    edges?: WorkspaceEdge[];
    registrationError?: string;
    homeWorkspaceId?: string | null;
  }) => boolean | Promise<boolean>;
};

type CombineOperand = "any" | "all";

export function WorkspaceCombinePoliciesDialog({
  open,
  onOpenChange,
  workspaceId,
  viewMode = "account",
  nodes,
  edges,
  anchorPolicyNode,
  initialSelectedIds,
  onCombined,
}: WorkspaceCombinePoliciesDialogProps): React.ReactElement {
  const isStrategyView = viewMode === "strategy";
  const combinable = useMemo(() => {
    if (!anchorPolicyNode) return [];
    return listCombinablePolicyNodes(anchorPolicyNode, nodes, edges);
  }, [anchorPolicyNode, edges, nodes]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bundleTitle, setBundleTitle] = useState("");
  const [entryKeepCombine, setEntryKeepCombine] = useState<CombineOperand>("all");
  const [entryVetoCombine, setEntryVetoCombine] = useState<CombineOperand>("any");
  const [previewing, setPreviewing] = useState(false);
  const [combining, setCombining] = useState(false);
  const [preview, setPreview] = useState<CombinePoliciesPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [policyVersions, setPolicyVersions] = useState<ReportStrategyPolicyVersion[]>([]);

  useEffect(() => {
    if (!open || !anchorPolicyNode) return;
    const combinableIdSet = new Set(
      listCombinablePolicyNodes(anchorPolicyNode, nodes, edges).map((n) => n.id)
    );
    const fromInitial = (initialSelectedIds ?? []).filter((id) => combinableIdSet.has(id));
    const ordered =
      fromInitial.length >= 1
        ? [
            ...(fromInitial.includes(anchorPolicyNode.id)
              ? [anchorPolicyNode.id, ...fromInitial.filter((id) => id !== anchorPolicyNode.id)]
              : [anchorPolicyNode.id, ...fromInitial]),
          ]
        : [anchorPolicyNode.id];
    const seen = new Set<string>();
    setSelectedIds(ordered.filter((id) => (seen.has(id) ? false : (seen.add(id), true))));
    setBundleTitle("");
    setEntryKeepCombine("all");
    setEntryVetoCombine("any");
    setPreview(null);
    setPreviewError(null);
  }, [open, anchorPolicyNode, edges, initialSelectedIds, nodes]);

  const strategyId = useMemo(() => {
    if (!anchorPolicyNode) return null;
    return strategyIdForCombineCandidate(anchorPolicyNode, nodes, edges);
  }, [anchorPolicyNode, edges, nodes]);

  useEffect(() => {
    if (!open || !strategyId) {
      setPolicyVersions([]);
      return;
    }
    let cancelled = false;
    void listReportStrategyPolicyVersions(strategyId)
      .then((resp) => {
        if (!cancelled) setPolicyVersions(resp.versions ?? []);
      })
      .catch(() => {
        if (!cancelled) setPolicyVersions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, strategyId]);

  const selectedNodesInOrder = useMemo(() => {
    const byId = new Map(combinable.map((n) => [n.id, n]));
    return selectedIds.map((id) => byId.get(id)).filter((n): n is WorkspaceNode => Boolean(n));
  }, [combinable, selectedIds]);

  /** Leaf policies for BE combine/preview (nested combined policies are expanded). */
  const expandedSourcePolicies = useMemo(
    () => expandCombineSelectionToSourcePolicies(selectedNodesInOrder, nodes, edges),
    [edges, nodes, selectedNodesInOrder]
  );

  /** Canvas parents: the nodes the user selected (policies and/or combined policies). */
  const canvasParentNodeIds = useMemo(
    () =>
      selectedNodesInOrder.map((n) =>
        isStrategyView ? n.sourceNodeId || n.id : n.id
      ),
    [isStrategyView, selectedNodesInOrder]
  );

  const adopted = useMemo(
    () => resolveAdoptedReportFromPolicySelection(selectedNodesInOrder, nodes, edges),
    [edges, nodes, selectedNodesInOrder]
  );

  const stackVisibility = useMemo((): CombineStackVisibility => {
    const versionById = new Map(policyVersions.map((v) => [v.id, v]));
    const matched = expandedSourcePolicies
      .map((n) => versionById.get(n.entityId))
      .filter((v): v is ReportStrategyPolicyVersion => Boolean(v));
    const treesLoaded =
      matched.length === expandedSourcePolicies.length &&
      matched.length > 0 &&
      matched.every((v) => v.policy_tree != null);
    if (treesLoaded) {
      return combineStackVisibilityFromPolicyVersions(matched);
    }
    return combineStackVisibilityFromSourceKinds(
      expandedSourcePolicies.map((n) => classifyCombinePolicySourceKind(n, nodes, edges))
    );
  }, [edges, expandedSourcePolicies, nodes, policyVersions]);

  const showKeepCombine = stackVisibility.showKeepCombine;
  const showVetoCombine = stackVisibility.showVetoCombine;

  const previewErrors = Array.isArray(preview?.errors) ? preview!.errors! : [];
  const isPreviewInvalid = Boolean(
    preview && (preview.ok === false || preview.valid === false || previewErrors.length > 0)
  );
  const previewReady = Boolean(preview && !isPreviewInvalid && !previewError);
  const resolvedCandleTf =
    typeof preview?.resolved_candle_timeframe === "string"
      ? preview.resolved_candle_timeframe.trim()
      : "";

  const toggleId = (id: string) => {
    setSelectedIds((prev) => {
      if (id === anchorPolicyNode?.id) return prev;
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
    setPreview(null);
    setPreviewError(null);
  };

  const combineOperandsBody = useCallback(() => {
    return {
      ...(showKeepCombine ? { entry_keep_combine: entryKeepCombine } : {}),
      ...(showVetoCombine ? { entry_veto_combine: entryVetoCombine } : {}),
    };
  }, [entryKeepCombine, entryVetoCombine, showKeepCombine, showVetoCombine]);

  const onPreview = useCallback(async () => {
    if (!strategyId || expandedSourcePolicies.length < MIN_COMBINED_POLICY_SOURCES) {
      toast.warning(`Select enough policies to yield at least ${MIN_COMBINED_POLICY_SOURCES} singular sources`);
      return;
    }
    setPreviewing(true);
    setPreviewError(null);
    try {
      const versionIds = expandedSourcePolicies.map((n) => n.entityId);
      const operands = combineOperandsBody();
      const resp = await previewCombineReportStrategyPolicies(
        strategyId,
        isStrategyView
          ? {
              sources: buildCombinePolicySources(expandedSourcePolicies),
              policy_version_ids: versionIds,
              ...operands,
            }
          : { policy_version_ids: versionIds, ...operands }
      );
      setPreview(resp);
      const errors = Array.isArray(resp.errors) ? resp.errors : [];
      const invalid = resp.ok === false || resp.valid === false || errors.length > 0;
      if (invalid) {
        setPreviewError(
          errors[0]?.message || "Preview returned validation errors. Adjust the selected policies."
        );
      }
    } catch (e) {
      setPreview(null);
      setPreviewError(e instanceof Error ? e.message : "Preview failed.");
    } finally {
      setPreviewing(false);
    }
  }, [combineOperandsBody, expandedSourcePolicies, isStrategyView, strategyId]);

  const onCombine = useCallback(async () => {
    if (!strategyId || expandedSourcePolicies.length < MIN_COMBINED_POLICY_SOURCES) {
      toast.warning(`Need at least ${MIN_COMBINED_POLICY_SOURCES} singular policies after expanding combined sources`);
      return;
    }
    if (!previewReady) {
      toast.warning("Preview combine before saving");
      return;
    }
    setCombining(true);
    try {
      const versionIds = expandedSourcePolicies.map((n) => n.entityId);
      const homeWorkspaceId = resolveCombineHomeWorkspaceId(selectedNodesInOrder, {
        viewMode,
        fallbackWorkspaceId: workspaceId,
      });
      const operands = combineOperandsBody();
      const resp = await combineReportStrategyPolicies(
        strategyId,
        isStrategyView
          ? {
              sources: buildCombinePolicySources(expandedSourcePolicies),
              policy_version_ids: versionIds,
              ...(bundleTitle.trim() ? { title: bundleTitle.trim() } : {}),
              ...(adopted.adoptedReportId ? { adoptedReportId: adopted.adoptedReportId } : {}),
              ...(homeWorkspaceId ? { homeWorkspaceId } : {}),
              ...operands,
              set_as_current: true,
            }
          : {
              policy_version_ids: versionIds,
              ...(bundleTitle.trim() ? { title: bundleTitle.trim() } : {}),
              workspaceId,
              sourcePolicyNodeIds: canvasParentNodeIds,
              ...(adopted.adoptedReportId ? { adoptedReportId: adopted.adoptedReportId } : {}),
              ...operands,
            }
      );
      const policyVersionId =
        resp.policy_version_id ||
        (typeof resp.policy_version === "object" &&
        resp.policy_version &&
        "id" in (resp.policy_version as object)
          ? String((resp.policy_version as { id: string }).id)
          : "");
      if (!policyVersionId) {
        throw new Error("Combine succeeded but no policy version id was returned.");
      }
      const workspaceNodeRaw =
        (resp as { workspace_node?: WorkspaceNode; workspaceNode?: WorkspaceNode }).workspace_node ??
        (resp as { workspaceNode?: WorkspaceNode }).workspaceNode;
      const workspaceEdges =
        (resp as { workspace_edges?: WorkspaceEdge[]; workspaceEdges?: WorkspaceEdge[] })
          .workspace_edges ??
        (resp as { workspaceEdges?: WorkspaceEdge[] }).workspaceEdges;
      const registrationError =
        resp.workspace_registration_error ??
        (resp as { workspaceRegistrationError?: { message?: string } }).workspaceRegistrationError;
      const resolvedFromResp =
        typeof (resp as { resolved_candle_timeframe?: string }).resolved_candle_timeframe === "string"
          ? (resp as { resolved_candle_timeframe: string }).resolved_candle_timeframe.trim()
          : resolvedCandleTf || null;
      const registered = await onCombined({
        policyVersionId,
        sourcePolicyNodeIds: canvasParentNodeIds,
        adoptedReportId: adopted.adoptedReportId,
        title: bundleTitle.trim() || null,
        resolvedCandleTimeframe: resolvedFromResp,
        homeWorkspaceId: resp.homeWorkspaceId ?? homeWorkspaceId,
        ...(workspaceNodeRaw ? { workspaceNode: workspaceNodeRaw as WorkspaceNode } : {}),
        ...(workspaceEdges ? { edges: workspaceEdges as WorkspaceEdge[] } : {}),
        ...(registrationError?.message
          ? { registrationError: String(registrationError.message) }
          : {}),
      });
      if (registered) {
        onOpenChange(false);
      }
    } catch (e) {
      presentMutationError(e, "Failed to combine policies");
    } finally {
      setCombining(false);
    }
  }, [
    adopted.adoptedReportId,
    bundleTitle,
    canvasParentNodeIds,
    combineOperandsBody,
    expandedSourcePolicies,
    isStrategyView,
    onCombined,
    onOpenChange,
    previewReady,
    resolvedCandleTf,
    selectedNodesInOrder,
    strategyId,
    viewMode,
    workspaceId,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-workspace-combine-policies-dialog=""
        className="max-w-lg gap-4 font-[family-name:var(--font-primary)]"
      >
        <DialogHeader>
          <DialogTitle className="text-[color:var(--brand-tropical-surf)]">Combine policies</DialogTitle>
          <DialogDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
            Merge single policies and/or existing combined policies. Combined selections expand to
            their source policies. The first selected item sets the report date range for later
            combined simulations.
          </DialogDescription>
        </DialogHeader>

        {!strategyId ? (
          <Alert>
            <AlertDescription>
              Could not resolve a strategy for this policy. Link the report under a strategy first.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="flex max-h-56 flex-col gap-2 overflow-y-auto rounded-[6px] border border-border p-2">
              {combinable.map((node) => {
                const checked = selectedIds.includes(node.id);
                const isAnchor = node.id === anchorPolicyNode?.id;
                const isCombined = node.type === "combined_policy";
                return (
                  <label
                    key={node.id}
                    className="flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 hover:bg-muted/40"
                  >
                    <Checkbox
                      checked={checked}
                      disabled={isAnchor}
                      onCheckedChange={() => toggleId(node.id)}
                      className="mt-0.5"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-gray-500 dark:text-white">
                        {workspaceNodeTitle(node)}
                        {isCombined ? " · Combined" : ""}
                      </span>
                      {isAnchor ? (
                        <span className="text-xs text-[color:var(--brand-tropical-surf)]">
                          First selected · adopts this report window
                        </span>
                      ) : isCombined ? (
                        <span className="text-xs text-gray-500 dark:text-sidebar-muted-foreground">
                          Expands to its source policies when combining
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>

            <p className="m-0 text-xs text-gray-500 dark:text-sidebar-muted-foreground">
              Adopted report window:{" "}
              <span className="text-[color:var(--brand-tropical-surf)]">
                {adopted.adoptedReportTitle || adopted.adoptedReportId || "—"}
              </span>
              {expandedSourcePolicies.length > 0 ? (
                <>
                  {" · "}
                  Singular sources:{" "}
                  <span className="text-[color:var(--brand-tropical-surf)]">
                    {expandedSourcePolicies.length}
                  </span>
                </>
              ) : null}
            </p>

            {stackVisibility.managementOnly && expandedSourcePolicies.length >= 2 ? (
              <p className="m-0 text-xs text-gray-500 dark:text-sidebar-muted-foreground">
                Exit Placement only — rules merge as management segments. Keep/Remove stacks are
                for Regime entry policies. Preview will flag conflicting stop/target overrides.
              </p>
            ) : null}

            {showKeepCombine ? (
              <Field>
                <FieldLabel className="text-[color:var(--brand-tropical-surf)]">
                  Keep stack
                </FieldLabel>
                <TradeFilterSelectFullWidth
                  value={entryKeepCombine}
                  onValueChange={(v) => {
                    setEntryKeepCombine(v as CombineOperand);
                    setPreview(null);
                    setPreviewError(null);
                  }}
                  options={[
                    { value: "all", label: "All keeps must match (AND)" },
                    { value: "any", label: "Any keep may match (OR)" },
                  ]}
                />
                <FieldDescription className="text-xs text-gray-500 dark:text-sidebar-muted-foreground">
                  How multiple keep-only segments combine.
                </FieldDescription>
              </Field>
            ) : null}

            {showVetoCombine ? (
              <Field>
                <FieldLabel className="text-[color:var(--brand-tropical-surf)]">
                  Remove stack
                </FieldLabel>
                <TradeFilterSelectFullWidth
                  value={entryVetoCombine}
                  onValueChange={(v) => {
                    setEntryVetoCombine(v as CombineOperand);
                    setPreview(null);
                    setPreviewError(null);
                  }}
                  options={[
                    {
                      value: "any",
                      label: "Remove if any remove rule triggers (default)",
                    },
                    {
                      value: "all",
                      label: "Remove only if all remove rules trigger",
                    },
                  ]}
                />
                <FieldDescription className="text-xs text-gray-500 dark:text-sidebar-muted-foreground">
                  Independent of Keep stack. Applies only to remove segments in the bundle.
                </FieldDescription>
              </Field>
            ) : null}

            <Field>
              <FieldLabel className="text-[color:var(--brand-tropical-surf)]">Bundle title</FieldLabel>
              <Input
                value={bundleTitle}
                onChange={(e) => setBundleTitle(e.target.value)}
                placeholder="Optional title"
              />
            </Field>

            {previewError ? (
              <Alert>
                <AlertDescription className="text-[color:var(--brand-glacial-red)]">
                  {previewError}
                </AlertDescription>
              </Alert>
            ) : null}
            {previewReady ? (
              <Alert>
                <AlertDescription>
                  Preview looks valid. You can save the combined policy.
                  {resolvedCandleTf ? (
                    <>
                      {" "}
                      Candle timeframe for budget/OHLC:{" "}
                      <span className="text-[color:var(--brand-tropical-surf)]">
                        {resolvedCandleTf}
                      </span>{" "}
                      (finest of sources).
                    </>
                  ) : null}
                </AlertDescription>
              </Alert>
            ) : null}
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={
              !strategyId ||
              previewing ||
              combining ||
              selectedNodesInOrder.length < 2 ||
              expandedSourcePolicies.length < MIN_COMBINED_POLICY_SOURCES
            }
            onClick={() => void onPreview()}
          >
            {previewing ? <Loader2 className="size-4 animate-spin" /> : null}
            Preview
          </Button>
          <Button
            type="button"
            variant="gradient"
            disabled={!strategyId || combining || !previewReady}
            onClick={() => void onCombine()}
          >
            {combining ? <Loader2 className="size-4 animate-spin" /> : null}
            Combine
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
